import { useEffect, useState } from "react";
import { Eye, EyeOff, QrCode } from "lucide-react";
import SpeaqSignIn from "../components/SpeaqSignIn";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { LIVE, tunnelState } from "@/lib/tunnel-link";
import { adoptSession } from "../lib/session";
import { ASKING, HERE, PAIR_FIRST, whereCanISignIn } from "@/lib/sign-in-place";
import { useTaal } from "@/contexts/TaalContext";
import Beeldmerk from "../components/Beeldmerk";


export default function LoginPage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const { login, googleLogin, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Is there anything here to sign in to. On a Mac and down a live tunnel,
  // yes. On the connect service before this phone has joined a machine, no,
  // and then the three sign-in methods are three ways to get 405.
  const [place, setPlace] = useState(ASKING);
  useEffect(() => {
    let stillMounted = true;
    if (tunnelState().where === LIVE) {
      setPlace(HERE);
      return () => { stillMounted = false; };
    }
    axios
      .get(`${API}/mode`)
      .then(() => stillMounted && setPlace(whereCanISignIn({ backendAnswered: true })))
      .catch(() => stillMounted && setPlace(whereCanISignIn({ backendAnswered: false })));
    return () => { stillMounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success(t("Aangemeld"));
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("Aanmelden mislukt"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.error(t("Vul eerst je e-mailadres in"));
      return;
    }
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      setForgotSent(true);
      toast.success(t("Herstellink verstuurd als het adres bestaat"));
    } catch {
      toast.error(t("Herstelmail versturen mislukt"));
    }
  };

  const handleGoogleLogin = async () => {
    toast.info("Google login requires a backend client ID. Configure GOOGLE_CLIENT_ID in .env");
  };

  // PUNT 1, 12 oktober 2026: kwam de weg terug naar de Mac pas na de wachttijd
  // van het opstarten, dan stond de telefoon hier al. Zodra het kanaal er is en
  // de Mac zegt wie je bent, hoort dit scherm weg te gaan.
  if (user && tunnelState().where === LIVE) return <Navigate to="/" replace />;

  // Los punt verticale overloop, 21 oktober 2026: de gloed van `.stellar-field`
  // loopt 25 procent onder het veld door en maakte de pagina 192 pixels te hoog.
  // Hier mag ook de y-as af, want de bovenkant van het veld is de bovenkant van het
  // venster. In de chat niet; daar staat het veld midden in een kolom.
  return (
    <div className="stellar-field overflow-y-clip min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="glass-panel w-full max-w-sm">
        <CardHeader className="text-center">
          {/* The mark, same size and place as the old logo.png, but the SVG
              from the chat and the sidebar: only the orange shapes, without
              the black square of the PNG (J8 follow-up, Frank 18 Oct 2026). */}
          <div className="flex justify-center mb-3">
            <Beeldmerk className="h-10 w-10" testId="inlog-beeldmerk" />
          </div>
          {/* The guide's own primary tagline rather than the version badge
              from the start page. A version number is something you look up
              once you are inside; this is the first sentence anybody reads. */}
          <div className="mb-3 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(255,107,53,0.28)] bg-[rgba(255,107,53,0.08)] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              {t("Een nieuw universum van intelligentie")}
            </span>
          </div>
          <CardTitle className="font-serif text-3xl font-normal leading-tight">{t("Welkom terug")}</CardTitle>
          <CardDescription className="text-muted-foreground">{t("Meld je aan bij je Novamund-account")}</CardDescription>
        </CardHeader>

        {/* Nothing at all until we know. Neither guess is harmless: assuming
            a backend is what put four failing buttons on Frank's phone, and
            assuming none would hide the password on a Mac that is slow. */}
        {place === ASKING && (
          <CardContent>
            <p className="text-xs text-center text-muted-foreground" data-testid="login-asking">
              {t("Een moment...")}
            </p>
          </CardContent>
        )}

        {/* No backend. Every sign-in method here would answer 405, so none of
            them is offered. The scan is not a fourth option next to them, it
            is the only road, and it is what makes the others work afterwards. */}
        {place === PAIR_FIRST && (
          <CardContent className="space-y-4" data-testid="login-pair-first">
            <p className="text-sm">
              {t("Deze telefoon is nog niet verbonden met je computer.")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("Novamund draait op je eigen machine, dus hier valt niets aan te melden tot de twee verbonden zijn. Open Novamund op je computer en scan de code die hij toont. Daarna meld je je zoals altijd aan met SPEAQ ID of je wachtwoord.")}
            </p>
            <Button
              type="button"
              className="w-full h-11 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))]"
              onClick={() => navigate("/scan")}
              data-testid="pair-first-scan-btn"
            >
              <QrCode className="h-4 w-4 mr-2" />
              {t("Scan de code op je computer")}
            </Button>
          </CardContent>
        )}

        {place === HERE && (
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("E-mail")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("jij@voorbeeld.nl")} required autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("Wachtwoord")}</Label>
              {/* Revealable. A phone hands this field to autofill, and a field
                  full of dots is a field you cannot check: on 2026-07-26 this
                  one carried 426 characters nobody had typed, and the only way
                  to find that out was a line in the server log. */}
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="pr-12" />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="tap-target absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? t("Wachtwoord verbergen") : t("Wachtwoord tonen")}
                  aria-pressed={showPassword}
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password.length > 64 && (
                <p className="text-xs text-corona" data-testid="password-length-warning">
                  This field holds {password.length} characters. That is almost
                  certainly not your password: check what autofill put there.
                </p>
              )}
            </div>
            {forgotSent && (
              <p className="text-xs text-[hsl(var(--nebula))]">{t("Kijk in je mail voor de herstelinstructies")}</p>
            )}
            {/* A link, so the violet voice. The shared link variant paints
                itself Supernova, and orange next to the orange Sign in button
                makes two things look equally like the thing to press. */}
            <Button type="button" variant="link" size="sm" className="p-0 h-auto text-xs text-[hsl(var(--nebula))]" onClick={handleForgotPassword}>
              {t("Wachtwoord vergeten?")}
            </Button>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            {/* The one primary CTA on this screen. Supernova at rest and
                Solar Flare on hover, which is what the guide says and not
                what the shared button variant does: that one dims Supernova
                to 90 percent, so hovering makes the button quieter. */}
            <Button type="submit" className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))]" disabled={isLoading}>
              {isLoading ? t("Aanmelden...") : t("Aanmelden")}
            </Button>
            <Separator className="my-1" />
            {/* SPEAQ ID, next to the password and never instead of it. The
                component shows a QR for the phone in your hand and a six-digit
                code for the phone this is running on; on iOS with both apps
                installed the digits are the only path that works. */}
            <SpeaqSignIn
              onSignedIn={(tokens) => {
                // Dezelfde weg naar binnen als koppelen, en om dezelfde reden.
                // Wat hier stond schreef de tokens en deed `navigate("/")`, een
                // client-side routewissel. De auth-context vraagt maar een keer
                // wie je bent, in een mount-effect, dus schermen die al stonden
                // hielden hun 401-data vast tot een herlaad. Dat is precies de
                // les van 26 juli die voor koppelen al in `adoptSession` zit en
                // waar de SPEAQ-weg langs was gelopen. Gezien op 6 augustus:
                // getekend op de telefoon, geldige tokens, en schermen die leeg
                // bleven.
                adoptSession(tokens);
              }}
            />
            {/* The way in that needs no password at all: the desktop is
                showing a code, and scanning it is what connects this phone to
                that machine. It sits above the other options because on a
                phone it is the shortest road, and it is the one that will
                carry the SPEAQ ID sign-in next. */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              onClick={() => navigate("/scan")}
              data-testid="scan-code-btn"
            >
              <QrCode className="h-4 w-4 mr-2" />
              {t("Scan de code op je computer")}
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={handleGoogleLogin}>
              {t("Doorgaan met Google")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {/* Links are the secondary voice, Nebula violet. Orange is
                  reserved for the thing you press, and a text link is not it. */}
              {t("Nog geen account?")} <Link to="/register" className="text-[hsl(var(--nebula))] hover:underline">{t("Registreren")}</Link>
            </p>
            <p className="text-xs text-muted-foreground">
              <Link to="/" className="hover:underline">{t("Doorgaan zonder account")}</Link>
            </p>
          </CardFooter>
        </form>
        )}
      </Card>
    </div>
  );
}
